<script setup lang="ts">
/**
 * 智慧客服救援系統 (Omnichannel Retention)
 * 解決方案：
 * 1. 整合 Exit-Intent 與 Idle 監控，在關鍵時刻（結帳、搜尋無效）主動介入
 * 2. 支援折扣碼一鍵複製，提高挽回率
 * 3. 雙通道切換：Live Chat 或 WhatsApp API
 */
import { useClipboard } from "@vueuse/core";
// 從全域 useState 獲取狀態 (來自 telemetry 插件)
const isVisible = useState("telemetry:rescue-active", () => false);
const rescueReason = useState(
  "telemetry:rescue-reason",
  () => "Ready to explore?",
);
const { copy, copied } = useClipboard();
const promoCode = "SAVE25EDU";
const close = () => (isVisible.value = false);
</script>
<template>
  <ClientOnly>
    <Teleport to="body">
      <!-- 救援控制台 (Bottom Right Floating Panel) -->
      <Transition name="slide-in">
        <div
          v-if="isVisible"
          class="fixed z-[400] bottom-6 right-6 w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-3xl shadow-[0_30px_100px_rgba(0,0,0,0.25)] border border-slate-100 overflow-hidden flex flex-col animate-fade-in-up"
        >
          <!-- 頂部區塊 -->
          <div class="bg-brand-900 p-8 text-white relative overflow-hidden">
            <div
              class="absolute -right-10 -top-10 w-40 h-40 bg-brand-600/30 rounded-full blur-3xl"
            ></div>
            <button
              @click="close"
              class="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            >
              <Icon name="ph:x-bold" />
            </button>
            <Icon
              name="ph:headset-fill"
              class="text-4xl text-action-400 mb-4"
            />
            <h4
              class="text-xl font-black italic tracking-tight uppercase leading-tight"
            >
              {{ rescueReason }}
            </h4>
            <p class="text-brand-200 text-xs font-medium mt-2 leading-relaxed">
              Our specialists are standing by to verify your student status or
              fix complex routes.
            </p>
          </div>
          <!-- 互動內容 -->
          <div class="p-8 space-y-6">
            <!-- 折扣誘餌 -->
            <div
              class="bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl p-4 flex items-center justify-between group cursor-pointer"
              @click="copy(promoCode)"
            >
              <div>
                <p
                  class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1"
                >
                  Copy Your Secret Code
                </p>
                <p
                  class="text-lg font-black text-slate-900 font-mono tracking-tighter"
                >
                  {{ promoCode }}
                </p>
              </div>
              <div
                class="w-10 h-10 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-brand-600 shadow-sm transition-transform group-hover:scale-110"
              >
                <Icon :name="copied ? 'ph:check-bold' : 'ph:copy-bold'" />
              </div>
            </div>
            <!-- 全通路按鈕 -->
            <div class="grid grid-cols-2 gap-3">
              <button
                class="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-brand-50 border border-brand-100 hover:bg-brand-100 transition-colors"
              >
                <Icon
                  name="ph:chat-circle-text-fill"
                  class="text-2xl text-brand-600"
                />
                <span class="text-[10px] font-black uppercase text-brand-900"
                  >Live Chat</span
                >
              </button>
              <button
                class="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-green-50 border border-green-100 hover:bg-green-100 transition-colors"
              >
                <Icon
                  name="ph:whatsapp-logo-fill"
                  class="text-2xl text-green-600"
                />
                <span class="text-[10px] font-black uppercase text-green-900"
                  >WhatsApp</span
                >
              </button>
            </div>
            <button
              @click="close"
              class="w-full text-center text-[10px] font-black text-slate-300 uppercase tracking-widest hover:text-slate-500 transition-colors"
            >
              Dismiss Rescue
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>
<style scoped>
.slide-in-enter-active,
.slide-in-leave-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-in-enter-from {
  transform: translateX(50px) translateY(20px);
  opacity: 0;
}
</style>
